import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useRoute } from "react-router5";
import { checkPermission, UserPermission } from "../../methods/permissionByRole";
import { userFullname } from "../../methods/utils";
import "pages/Styles/ProfileV2.sass";
import { finishAuth } from "../../methods/finishAuth";
import { loadRoles, getBusinessRole } from "../../utils/workWithRoles";
import { httpClient } from "../../funcs";
import { notifyAboutError } from "../../methods/notifyUser";
import UpdateUserFields from "./UpdateUserFields";

type Props = {
    $store: any,
    $commitToStore: Function,
    forceOpenProfileEdit?: boolean,
    onForceOpenProfileEditConsumed?: () => void,
}

function accountTypeLabel(user: any): string {
    if (user?.role === "customer") {
        return "Личный аккаунт";
    }
    if (user?.role === "entrepreneur") {
        return "Бизнес аккаунт";
    }
    return "Аккаунт";
}

let AccountHeader: any = function AccountHeader(props: Props) {
    const $router = useRoute();
    const [businessRole, setBusinessRole] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [myUserId, setMyUserId] = useState<string | null>(null);

    useEffect(() => {
        if (props.$store.userSession) {
            loadUserRoles();
        }
    }, [props.$store.userSession]);

    async function loadUserRoles() {
        const { grantedToYouRoles, users, currentUserId, myUserId } = await loadRoles(
            props.$store.userSession?._id,
            props
        );
        setBusinessRole(getBusinessRole(grantedToYouRoles));
        setUsers(users);
        setCurrentUserId(currentUserId ?? null);
        setMyUserId(myUserId ?? null);
    }

    async function switchToAccount(target: any) {
        try {
            const targetType = target.type === "role" ? "role" : "user";
            const { data } = await httpClient.post("/user/switch-account", {
                target_id: target._id,
                target_type: targetType,
            });

            const tokenData = data.refresh_token
                ? { access_token: data.access_token, refresh_token: data.refresh_token }
                : { access_token: data.access_token };

            await finishAuth(props, $router, tokenData, "profile");
            props.$commitToStore({
                role: null
            });
            await loadUserRoles();
        } catch (error) {
            notifyAboutError(props, error);
        }
    }

    function renderSwitchRow(label: string, displayName: string, target: any) {
        return (
            <div className="account-header-switch-row Body_16_v2_secondary">
                <span>{label}: {displayName}</span>
                <button
                    type="button"
                    className="ui-kit-v2 action-button secondary inline account-header-switch-btn"
                    onClick={() => switchToAccount(target)}
                >
                    <span className="button-text">Войти</span>
                </button>
            </div>
        );
    }

    function renderAccountHeader() {
        const currentUser = props.$store.userSession;
        const activeUserId = currentUserId ?? currentUser?._id;
        const isLoggedInViaRole = currentUser?.role_id != null;
        const isBusinessAccount = isLoggedInViaRole || currentUser?.employee_id != null;
        const isOwner = checkPermission(props, UserPermission.OWNER);

        const personalAccount =
            (myUserId && users.find((u: any) => u._id === myUserId)) ||
            users.find((u: any) => u._id !== activeUserId && u.role === "customer") ||
            users.find((u: any) => u._id !== activeUserId);

        const otherAccounts = users.filter((u: any) => u._id !== activeUserId);
        const isEmployeeInBusiness = isBusinessAccount && !isOwner && !!personalAccount;

        const showUpdateFields =
            checkPermission(props, UserPermission.OWNER) && !isLoggedInViaRole;

        const updateUserFieldsButton = showUpdateFields ? (
            <UpdateUserFields
                forceOpen={props.forceOpenProfileEdit}
                onForceOpenConsumed={props.onForceOpenProfileEditConsumed}
            />
        ) : null;

        // Если владелец в бизнес-аккаунте без других аккаунтов на номере — только имя
        if (isBusinessAccount && isOwner && otherAccounts.length === 0) {
            return (
                <div className="account-header-row">
                    <div className="account-header-title H3_21_v2">
                        {userFullname(currentUser)}
                    </div>
                    <div className="account-header-actions">
                        {updateUserFieldsButton}
                    </div>
                </div>
            );
        }

        // Сотрудник в бизнес-аккаунте — переключение в личный
        if (isEmployeeInBusiness && personalAccount) {
            return (
                <div>
                    <div className="account-header-row">
                        <div className="account-header-title H3_21_v2">
                            Бизнес аккаунт: {userFullname(currentUser)}
                        </div>
                        <div className="account-header-actions">
                            {updateUserFieldsButton}
                        </div>
                    </div>
                    {renderSwitchRow(
                        accountTypeLabel(personalAccount),
                        userFullname(personalAccount),
                        personalAccount
                    )}
                </div>
            );
        }

        // Личный аккаунт + подтверждённая роль в чужом бизнесе
        if (!isBusinessAccount && businessRole && businessRole.status === "CONFIRMED") {
            return (
                <div>
                    <div className="account-header-row">
                        <div className="account-header-title H3_21_v2">
                            Личный аккаунт: {userFullname(currentUser)}
                        </div>
                        <div className="account-header-actions">
                            {updateUserFieldsButton}
                        </div>
                    </div>
                    {renderSwitchRow(
                        "Бизнес аккаунт",
                        userFullname(businessRole.granted_by),
                        businessRole
                    )}
                </div>
            );
        }

        // Два (или более) аккаунта на одном номере — физ. лицо и юр. лицо
        if (otherAccounts.length > 0) {
            return (
                <div>
                    <div className="account-header-row">
                        <div className="account-header-title H3_21_v2">
                            {accountTypeLabel(currentUser)}: {userFullname(currentUser)}
                        </div>
                        <div className="account-header-actions">
                            {updateUserFieldsButton}
                        </div>
                    </div>
                    {otherAccounts.map((account: any) =>
                        renderSwitchRow(
                            accountTypeLabel(account),
                            userFullname(account),
                            account
                        )
                    )}
                </div>
            );
        }

        return (
            <div className="account-header-row">
                <div className="account-header-title H3_21_v2">
                    {userFullname(currentUser)}
                </div>
                <div className="account-header-actions">
                    {updateUserFieldsButton}
                </div>
            </div>
        );
    }

    return renderAccountHeader();
}

AccountHeader = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(AccountHeader)

export default AccountHeader;
